/**
 * OnboardingGuard
 * Protects routes and redirects users who need onboarding
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboardingContext } from '@/providers/OnboardingProvider';
import { useIsSessionExpired } from '@/services/sessionManager';
import { Loader2 } from 'lucide-react';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, needsOnboarding, status, scenario } = useOnboardingContext();
  const isSessionExpired = useIsSessionExpired();

  // Handle returnUrl redirect when user doesn't need onboarding
  useEffect(() => {
    // Only check returnUrl when loading is complete and user doesn't need onboarding
    if (!isLoading && !needsOnboarding) {
      const returnUrl = localStorage.getItem('returnUrl');
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      
      if (returnUrl) {
        // If we're already at the target URL, just clear it to prevent loops
        if (returnUrl === currentPath) {
          if (import.meta.env.DEV) console.log('✅ Already at target URL, clearing returnUrl:', currentPath);
          localStorage.removeItem('returnUrl');
        } else {
          // Only redirect if returnUrl is different from current path
          if (import.meta.env.DEV) console.log('🔄 User authenticated and onboarding complete - redirecting to saved URL:', returnUrl);
          localStorage.removeItem('returnUrl');
          navigate(returnUrl, { replace: true });
        }
      }
    }
  }, [isLoading, needsOnboarding, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (import.meta.env.DEV) console.log('🔍 OnboardingGuard Check:', {
      isLoading,
      needsOnboarding,
      hasStatus: !!status,
      currentStage: status?.current_stage,
      pathname: location.pathname,
      isOnboardingPage: location.pathname.startsWith('/onboarding')
    });

    // If on onboarding page but DON'T need it (completed), redirect to saved URL or dashboard
    if (location.pathname.startsWith('/onboarding') && !isLoading && !needsOnboarding) {
      const returnUrl = localStorage.getItem('returnUrl');
      if (returnUrl) {
        if (import.meta.env.DEV) console.log('✅ Onboarding complete - redirecting to saved URL:', returnUrl);
        localStorage.removeItem('returnUrl');
        navigate(returnUrl, { replace: true });
      } else {
        if (import.meta.env.DEV) console.log('✅ Onboarding complete - redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
      return;
    }

    // Don't redirect if already on onboarding page and still need onboarding
    if (location.pathname.startsWith('/onboarding')) {
      if (import.meta.env.DEV) console.log('📍 Already on onboarding page - staying here');
      return;
    }

    // Redirect to onboarding if needed
    if (!isLoading && needsOnboarding && status) {
      if (import.meta.env.DEV) console.log('🚀 Redirecting to onboarding:', {
        scenario,
        stage: status.current_stage,
        annualUpdateOverdue: status.annual_update_overdue,
        fromPath: location.pathname
      });
      
      // Navigate to onboarding with scenario context
      navigate('/onboarding', {
        state: {
          scenario,
          fromPath: location.pathname
        },
        replace: true
      });
    } else if (!isLoading && needsOnboarding && !status) {
      console.warn('⚠️ Onboarding needed but no status available - forcing redirect');
      navigate('/onboarding', {
        state: {
          scenario: 'first-time-user',
          fromPath: location.pathname
        },
        replace: true
      });
    }
  }, [isLoading, needsOnboarding, status, scenario, navigate, location.pathname]);

  // If session is expired, render children as-is — SessionExpiredModal (z-300) handles UX
  if (isSessionExpired) {
    return <>{children}</>;
  }

  // Show loading state while checking onboarding
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-heritage-blue mx-auto" />
          <p className="text-sm text-muted-foreground">Checking your onboarding status...</p>
        </div>
      </div>
    );
  }

  // Render children if onboarding not needed or already on onboarding page
  return <>{children}</>;
}
